import type { SearchFilters, PersonaFilters } from "./filters";
import { normalizeToArray } from "./filters";
import { matchesLocation } from "./location-utils";
import { PRIMARY_GENRE_COUNT, artistGenresMatchFilter } from "./genre-utils";
import { todayKey, isInDateRange } from "./date-utils";

/**
 * Centralized Filter Engine
 *
 * Pure, modular filter functions for all search/filter functionality.
 * Used across: home search, advanced filters, personas, browse pages.
 *
 * All functions are pure (no side effects) and testable.
 */

// Re-export types for convenience
export type { SearchFilters, PersonaFilters };

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Generic item with shows (used by all entity types)
 */
export interface ItemWithShows {
  id: number;
  name: string;
  shows: ShowForFiltering[];
}

/**
 * Artist with shows structure
 */
export interface ArtistWithShows extends ItemWithShows {
  genres?: any;  // Can be string[], JSONB, or null
  careerStage?: string | null;
  setArtistGroupId?: string | null;
  fanGender?: Array<{ title: string; value: number }> | null;
  fanEthnicity?: Array<{ title: string; value: number }> | null;
  fanAge?: Array<{ title: string; value: number }> | null;
  fanIncome?: Array<{ title: string; value: number }> | null;
  fanAttributes?: Array<{ title: string; value: number }> | null;
}

/**
 * Venue with shows structure
 */
export interface VenueWithShows extends ItemWithShows {
  venueType?: string | null;
  city?: string | null;
  state?: string | null;
}

/**
 * Market with shows structure
 */
export interface MarketWithShows extends ItemWithShows {
  states?: string[] | null;
}

/**
 * Show structure for filtering
 */
export interface ShowForFiltering {
  id: number;
  date: string;
  sponsorable?: boolean | null;
  venue: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
    venueType?: string | null;
    dma?: {
      id: number;
      name: string;
    } | null;
  };
  artist?: {
    id: number;
    name: string;
    genres?: any;
    careerStage?: string | null;
  } | null;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Normalize genres from various formats to string array
 * @param limit - Optional limit to only consider top N genres (e.g. PRIMARY_GENRE_COUNT)
 */
function normalizeGenres(genres: any, limit?: number): string[] {
  if (!genres) return [];
  let arr: string[];
  if (Array.isArray(genres)) arr = genres.map(g => String(g).toLowerCase());
  else if (typeof genres === 'string') arr = [genres.toLowerCase()];
  else return [];
  return limit ? arr.slice(0, limit) : arr;
}

/**
 * Check if an array of genres matches any of the search genres
 * Only considers primary genres (top N by relevance) to avoid false positives
 */
function genresMatch(itemGenres: any, searchGenres: string[]): boolean {
  if (searchGenres.length === 0) return true;
  const normalized = normalizeGenres(itemGenres, PRIMARY_GENRE_COUNT);
  if (normalized.length === 0) return false;

  return searchGenres.some(searchGenre =>
    normalized.some(itemGenre => itemGenre.includes(searchGenre.toLowerCase()))
  );
}

/**
 * Check if item name matches search query (case-insensitive partial match)
 */
function nameMatches(itemName: string, searchQuery: string): boolean {
  if (!searchQuery || !searchQuery.trim()) return true;
  return itemName.toLowerCase().includes(searchQuery.toLowerCase().trim());
}

/**
 * Check if date is within range
 */
function dateInRange(showDate: string, dateFrom?: string, dateTo?: string): boolean {
  if (!dateFrom && !dateTo) return true;

  const date = new Date(showDate);
  if (dateFrom && date < new Date(dateFrom)) return false;
  if (dateTo && date > new Date(dateTo)) return false;

  return true;
}

/**
 * Calculate persona match percentage for an artist
 * Uses per-value granular matching: each selected demographic value is scored individually.
 * Over-index threshold > 1.05 filters out noise in the 1.0-1.05 range.
 */
function calculatePersonaMatch(artist: ArtistWithShows, personaFilters: PersonaFilters): number {
  const demographicKeys: (keyof PersonaFilters)[] = [
    'fanGender', 'fanEthnicity', 'fanAge', 'fanIncome', 'fanAttributes',
  ];

  let totalCriteria = 0;
  let matchedCriteria = 0;

  for (const key of demographicKeys) {
    const targetValues = personaFilters[key];
    if (!targetValues || targetValues.length === 0) continue;

    const artistData = artist[key as keyof ArtistWithShows];
    if (!Array.isArray(artistData)) {
      totalCriteria += targetValues.length;
      continue;
    }

    for (const targetValue of targetValues) {
      totalCriteria++;
      const normalizedTarget = targetValue.toLowerCase().trim();
      const hasMatch = artistData.some((item: any) => {
        if (!item || typeof item !== 'object') return false;
        return item.title?.toLowerCase().trim() === normalizedTarget && item.value > 1.05;
      });
      if (hasMatch) matchedCriteria++;
    }
  }

  return totalCriteria > 0 ? (matchedCriteria / totalCriteria) * 100 : 100;
}

// ============================================================================
// MODULAR FILTER FUNCTIONS
// ============================================================================

/**
 * Filter by artist name (partial, case-insensitive match)
 * For artists: checks item.name directly
 * For venues/markets: checks if any show's artist matches
 */
export function filterByArtistName<T extends ItemWithShows>(
  items: T[],
  artistName?: string
): T[] {
  if (!artistName || !artistName.trim()) return items;

  return items.filter(item => {
    // For artists (no 'capacity' or 'states' property), check item name directly
    // Note: can't use 'city' — artists also have a city field
    if (!('capacity' in item) && !('states' in item)) {
      return nameMatches(item.name, artistName);
    }

    // For venues/markets, check if any show's artist matches
    return item.shows.some(show =>
      show.artist && nameMatches(show.artist.name, artistName)
    );
  });
}

/**
 * Filter by venue name (partial, case-insensitive match)
 */
export function filterByVenueName<T extends ItemWithShows>(
  items: T[],
  venueName?: string
): T[] {
  if (!venueName || !venueName.trim()) return items;

  return items.filter(item => {
    // For venues, check the venue name directly
    // Note: can't use 'city' — artists also have a city field
    if ('capacity' in item) {
      return nameMatches(item.name, venueName);
    }

    // For artists/markets, check if any show's venue matches
    return item.shows.some(show => nameMatches(show.venue.name, venueName));
  });
}

/**
 * Filter by market name (partial, case-insensitive match)
 */
export function filterByMarketName<T extends ItemWithShows>(
  items: T[],
  marketName?: string
): T[] {
  if (!marketName || !marketName.trim()) return items;

  return items.filter(item => {
    // For markets, check the market name directly
    if ('states' in item) {
      return nameMatches(item.name, marketName);
    }

    // For artists/venues, check if any show's market matches
    return item.shows.some(show =>
      show.venue.dma && nameMatches(show.venue.dma.name, marketName)
    );
  });
}

/**
 * Filter by genre (matches if item has ANY of the search genres)
 */
export function filterByGenre<T extends ArtistWithShows>(
  items: T[],
  genre?: string | string[]
): T[] {
  const genres = normalizeToArray(genre);
  if (genres.length === 0) return items;

  return items.filter(item => genresMatch(item.genres, genres));
}

/**
 * Filter by career stage (matches if item has ANY of the search stages)
 */
export function filterByCareerStage<T extends ArtistWithShows>(
  items: T[],
  careerStage?: string | string[]
): T[] {
  const stages = normalizeToArray(careerStage);
  if (stages.length === 0) return items;

  return items.filter(item => {
    if (!item.careerStage) return false;
    return stages.some(stage =>
      item.careerStage?.toLowerCase() === stage.toLowerCase()
    );
  });
}

/**
 * Filter by SET Artist status (filters based on set_artist_group_id presence)
 * @param items - Array of artists with shows
 * @param setArtist - "yes" to include only SET Artists, "no" to exclude SET Artists
 */
export function filterBySetArtist<T extends ArtistWithShows>(
  items: T[],
  setArtist?: string
): T[] {
  if (!setArtist) return items;

  return items.filter(item => {
    const isSetArtist = item.setArtistGroupId != null && item.setArtistGroupId !== '';
    return setArtist === 'yes' ? isSetArtist : !isSetArtist;
  });
}

/**
 * Filter by venue type (for venues or items with shows at specific venue types)
 */
export function filterByVenueType<T extends ItemWithShows>(
  items: T[],
  venueType?: string | string[]
): T[] {
  const types = normalizeToArray(venueType);
  if (types.length === 0) return items;

  return items.filter(item => {
    // For venues, check the venue type directly
    if ('venueType' in item) {
      const venueItem = item as unknown as VenueWithShows;
      return venueItem.venueType && types.some(type =>
        venueItem.venueType?.toLowerCase() === type.toLowerCase()
      );
    }

    // For artists/markets, check if any show's venue matches
    return item.shows.some(show =>
      show.venue.venueType && types.some(type =>
        show.venue.venueType?.toLowerCase() === type.toLowerCase()
      )
    );
  });
}

/**
 * Filter by location (city, state, or market)
 * Uses existing location-utils for comprehensive matching
 */
export function filterByLocation<T extends ItemWithShows>(
  items: T[],
  location?: string
): T[] {
  if (!location || !location.trim()) return items;

  return items.filter(item => {
    // For venues, check venue location directly
    if ('city' in item && 'state' in item) {
      const venueItem = item as unknown as VenueWithShows;
      return matchesLocation(location, venueItem.city, venueItem.state);
    }

    // For markets, check market name and states
    if ('states' in item) {
      const marketItem = item as unknown as MarketWithShows;
      // Check market name
      if (matchesLocation(location, null, null, marketItem.name)) return true;
      // Check market states
      if (marketItem.states && marketItem.states.some(state =>
        matchesLocation(location, null, state)
      )) return true;
    }

    // For all items, check if any show's venue location matches
    return item.shows.some(show =>
      matchesLocation(location, show.venue.city, show.venue.state, show.venue.dma?.name)
    );
  });
}

/**
 * Filter by city (exact or partial match on venue city)
 */
export function filterByCity<T extends ItemWithShows>(
  items: T[],
  city?: string
): T[] {
  if (!city || !city.trim()) return items;
  const searchCity = city.toLowerCase().trim();

  return items.filter(item => {
    // For venues, check venue city directly
    if ('city' in item) {
      const venueItem = item as unknown as VenueWithShows;
      return venueItem.city?.toLowerCase().includes(searchCity);
    }

    // For artists/markets, check if any show's venue city matches
    return item.shows.some(show =>
      show.venue.city?.toLowerCase().includes(searchCity)
    );
  });
}

/**
 * Filter by state (supports both abbreviations and full names)
 */
export function filterByState<T extends ItemWithShows>(
  items: T[],
  state?: string
): T[] {
  if (!state || !state.trim()) return items;

  return items.filter(item => {
    // For venues, check venue state directly
    if ('state' in item) {
      const venueItem = item as unknown as VenueWithShows;
      return matchesLocation(state, null, venueItem.state);
    }

    // For artists/markets, check if any show's venue state matches
    return item.shows.some(show =>
      matchesLocation(state, null, show.venue.state)
    );
  });
}

/**
 * Filter by DMA IDs (HARD FILTER - exact match only)
 * This is used for persona target markets - completely filters out shows not in selected DMAs
 *
 * IMPORTANT: This is a hard filter and does NOT contribute to match percentage
 */
export function filterByDmaIds<T extends ItemWithShows>(
  items: T[],
  dmaIds?: number[]
): T[] {
  if (!dmaIds || dmaIds.length === 0) return items;

  return items.filter(item => {
    // Keep item if it has at least one show in one of the target DMAs
    return item.shows.some(show => {
      const showDmaId = show.venue.dma?.id;

      // If show has no DMA, exclude it (hard filter)
      if (!showDmaId) return false;

      // Check if show's DMA is in the target list
      return dmaIds.includes(showDmaId);
    });
  });
}

/**
 * Filter by date range (show date must be within range)
 */
export function filterByDateRange<T extends ItemWithShows>(
  items: T[],
  dateFrom?: string,
  dateTo?: string
): T[] {
  if (!dateFrom && !dateTo) return items;

  return items.filter(item => {
    // Keep item if it has at least one show in the date range
    return item.shows.some(show => dateInRange(show.date, dateFrom, dateTo));
  });
}

/**
 * Filter by sponsorable status
 */
export function filterBySponsorable<T extends ItemWithShows>(
  items: T[],
  sponsorable?: boolean
): T[] {
  if (sponsorable === undefined || sponsorable === null) return items;

  return items.filter(item => {
    // Keep item if it has at least one sponsorable show
    return item.shows.some(show => show.sponsorable === sponsorable);
  });
}

/**
 * Filter by persona/audience demographics
 * Uses AND logic: artist must match ALL active demographic filters
 */
export function filterByPersona<T extends ArtistWithShows>(
  items: T[],
  personaFilters?: PersonaFilters,
  minMatchPercentage: number = 0
): T[] {
  if (!personaFilters) return items;

  // Check if any persona filters are active
  const hasActiveFilters = Object.values(personaFilters).some(
    values => Array.isArray(values) && values.length > 0
  );

  if (!hasActiveFilters) return items;

  return items.filter(item => {
    const matchPercentage = calculatePersonaMatch(item, personaFilters);
    return matchPercentage >= minMatchPercentage;
  });
}

// ============================================================================
// SHOW-LEVEL FILTER FUNCTIONS
// ============================================================================

/**
 * Configuration for show-level filtering.
 * These are the "data filters" that apply to individual shows.
 */
export interface ShowFilterConfig {
  dateFrom?: string;
  dateTo?: string;
  genre?: string | string[];
  location?: string;
  artistName?: string;
  venueName?: string;
  careerStage?: string | string[];
  venueType?: string | string[];
  setArtist?: string;
}

/**
 * Options controlling show-level filtering behavior.
 */
export interface ShowTrimOptions {
  /** Exclude shows with dates before today. Default: true */
  excludePastShows?: boolean;
  /** Skip location re-filtering (for market-modal multi-state DMA correctness). Default: false */
  skipLocation?: boolean;
}

/**
 * Filter an array of shows by data-level criteria:
 * upcoming (exclude past), date range, genre, location, artist name, venue name.
 *
 * Works with any show object that has at minimum { date, artist: { name, genres }, venue: { name, city?, state? } }.
 */
export function trimShowsByFilters<T extends { date: any; artist: any; venue: any }>(
  shows: T[],
  config: ShowFilterConfig,
  options: ShowTrimOptions = {},
): T[] {
  const { excludePastShows = true, skipLocation = false } = options;

  // Normalize genre to a comma-separated string for artistGenresMatchFilter
  const genreFilterString = config.genre
    ? (Array.isArray(config.genre) ? config.genre.join(', ') : config.genre)
    : '';
  const hasGenre = genreFilterString.length > 0;

  // Normalize career stage filter
  const careerStages = normalizeToArray(config.careerStage);
  const hasCareerStage = careerStages.length > 0;

  // Normalize venue type filter
  const venueTypes = normalizeToArray(config.venueType);
  const hasVenueType = venueTypes.length > 0;

  const today = excludePastShows ? todayKey() : null;

  return shows.filter(show => {
    const dateKey = (show.date as any as string).slice(0, 10);

    // Exclude past shows
    if (today && dateKey < today) return false;

    // Date range
    if (config.dateFrom || config.dateTo) {
      if (!isInDateRange(dateKey, config.dateFrom, config.dateTo)) return false;
    }

    // Genre
    if (hasGenre) {
      if (!artistGenresMatchFilter(show.artist?.genres, genreFilterString)) return false;
    }

    // Career stage (artist property, applied at show level)
    if (hasCareerStage) {
      if (!show.artist?.careerStage) return false;
      if (!careerStages.some(stage => show.artist.careerStage.toLowerCase() === stage.toLowerCase())) return false;
    }

    // Location (skip for market-modal multi-state DMA case)
    if (!skipLocation && config.location) {
      if (!matchesLocation(
        config.location,
        show.venue?.city,
        show.venue?.state,
        show.venue?.dma?.name ?? show.venue?.market?.name ?? null,
      )) return false;
    }

    // Artist name
    if (config.artistName && config.artistName.trim()) {
      if (!show.artist?.name?.toLowerCase().includes(config.artistName.toLowerCase().trim())) return false;
    }

    // Venue name
    if (config.venueName && config.venueName.trim()) {
      if (!show.venue?.name?.toLowerCase().includes(config.venueName.toLowerCase().trim())) return false;
    }

    // Venue type
    if (hasVenueType) {
      const vt = show.venue?.venueType;
      if (!vt || !venueTypes.some(type => vt.toLowerCase() === type.toLowerCase())) return false;
    }

    // SET Artist filter
    if (config.setArtist) {
      const isSetArtist = show.artist?.setArtistGroupId != null && show.artist?.setArtistGroupId !== '';
      if (config.setArtist === 'yes' && !isSetArtist) return false;
      if (config.setArtist === 'no' && isSetArtist) return false;
    }

    return true;
  });
}

/**
 * Convenience wrapper: trim each entity's `.shows` array, then remove entities with 0 remaining shows.
 * Replaces the common `filtered.map(...).filter(v => v.shows.length > 0)` pattern in browse pages.
 */
export function trimEntityShows<T extends { shows: any[] }>(
  entities: T[],
  config: ShowFilterConfig,
  options?: ShowTrimOptions,
): T[] {
  return entities
    .map(entity => ({
      ...entity,
      shows: trimShowsByFilters(entity.shows, config, options),
    }))
    .filter(entity => entity.shows.length > 0);
}

/**
 * Show-level DMA hard filter.
 * Removes shows whose venue is NOT in one of the specified DMAs.
 * Equivalent to filterByDmaIds but operates on a flat show array rather than entities.
 */
export function filterShowsByDma<T extends { venue: any }>(
  shows: T[],
  dmaIds?: number[],
): T[] {
  if (!dmaIds || dmaIds.length === 0) return shows;

  return shows.filter(show => {
    const venueDmaId = show.venue?.dmaId ?? show.venue?.dma?.id;
    if (!venueDmaId) return false;
    return dmaIds.includes(venueDmaId);
  });
}

// ============================================================================
// ORCHESTRATOR FUNCTION
// ============================================================================

/**
 * Apply all active filters to a list of items
 * This is the main entry point for filtering
 *
 * @param items - Array of items to filter (artists, venues, or markets with shows)
 * @param filters - SearchFilters object with all filter criteria
 * @returns Filtered array of items
 */
export function applyAllFilters<T extends ItemWithShows>(
  items: T[],
  filters: SearchFilters
): T[] {
  let filtered = items;

  // Apply name filters
  if (filters.artistName) {
    filtered = filterByArtistName(filtered, filters.artistName);
  }
  if (filters.venueName) {
    filtered = filterByVenueName(filtered, filters.venueName);
  }
  if (filters.marketName) {
    filtered = filterByMarketName(filtered, filters.marketName);
  }

  // Apply location filters
  if (filters.location) {
    filtered = filterByLocation(filtered, filters.location);
  }
  if (filters.city) {
    filtered = filterByCity(filtered, filters.city);
  }
  if (filters.state) {
    filtered = filterByState(filtered, filters.state);
  }
  // HARD FILTER: DMA IDs (persona target markets) - excludes shows entirely
  if (filters.targetDmaIds) {
    filtered = filterByDmaIds(filtered, filters.targetDmaIds);
  }

  // Apply date filters
  if (filters.dateFrom || filters.dateTo) {
    filtered = filterByDateRange(filtered, filters.dateFrom, filters.dateTo);
  }

  // Apply sponsorable filter
  if (filters.sponsorable !== undefined) {
    filtered = filterBySponsorable(filtered, filters.sponsorable);
  }

  // Apply artist-specific filters (only if items are artists)
  if (filtered.length > 0 && 'genres' in filtered[0]) {
    const artistFiltered = filtered as unknown as ArtistWithShows[];

    if (filters.genre) {
      filtered = filterByGenre(artistFiltered, filters.genre) as unknown as T[];
    }
    if (filters.careerStage) {
      filtered = filterByCareerStage(artistFiltered, filters.careerStage) as unknown as T[];
    }
    if (filters.personaFilters) {
      filtered = filterByPersona(
        artistFiltered,
        filters.personaFilters,
        filters.minMatchPercentage || 0
      ) as unknown as T[];
    }
  }

  // Apply venue type filter (works for venues, shows/artists with venue data, and markets)
  if (filters.venueType) {
    filtered = filterByVenueType(filtered, filters.venueType);
  }

  return filtered;
}

/**
 * Get count of items matching filters (without returning the items)
 * Useful for pagination and counts
 */
export function getFilteredCount<T extends ItemWithShows>(
  items: T[],
  filters: SearchFilters
): number {
  return applyAllFilters(items, filters).length;
}

/**
 * Apply filters and return paginated results
 */
export function applyFiltersWithPagination<T extends ItemWithShows>(
  items: T[],
  filters: SearchFilters,
  page: number = 1,
  limit: number = 20
): { items: T[]; total: number; page: number; totalPages: number } {
  const filtered = applyAllFilters(items, filters);
  const total = filtered.length;
  const totalPages = Math.ceil(total / limit);
  const offset = (page - 1) * limit;
  const paginated = filtered.slice(offset, offset + limit);

  return {
    items: paginated,
    total,
    page,
    totalPages,
  };
}
